// components/ActiveAlerts.tsx
// Display and manage active system alerts

import React, { useState, useEffect } from 'react';
import { getActiveAlerts, resolveAlert } from '../services/analytics';
import type { Alert } from '../types';
import './ActiveAlerts.css';

export const ActiveAlerts: React.FC = () => {
    const [alerts, setAlerts] = useState<Alert[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadActiveAlerts();
    }, []);

    const loadActiveAlerts = async () => {
        try {
            const data = await getActiveAlerts();
            setAlerts(data);
        } catch (error) {
            console.error('Error loading alerts:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleResolveAlert = async (alertId: string) => {
        try {
            await resolveAlert(alertId);
            setAlerts(alerts.filter(a => a.id !== alertId));
        } catch (error) {
            console.error('Error resolving alert:', error);
        }
    };

    if (loading) {
        return (
            <div className="active-alerts">
                <h3>Active Alerts</h3>
                <div className="loading-state">Loading...</div>
            </div>
        );
    }

    if (alerts.length === 0) {
        return (
            <div className="active-alerts no-alerts">
                <h3>🔔 Active Alerts</h3>
                <p className="success-message">✅ No active alerts</p>
            </div>
        );
    }

    return (
        <div className="active-alerts">
            <h3>🔔 Active Alerts ({alerts.length})</h3>

            <div className="alerts-list">
                {alerts.map(alert => (
                    <div
                        key={alert.id}
                        className={`alert-card ${alert.severity}`}
                    >
                        <div className="alert-header">
                            <span className="alert-icon">
                                {alert.severity === 'critical' ? '🚨' : '⚠️'}
                            </span>
                            <span className="alert-type">{alert.type}</span>
                            <span className="alert-time">
                                {alert.timestamp.toLocaleString()}
                            </span>
                        </div>

                        <div className="alert-message">{alert.message}</div>

                        <button
                            className="resolve-button"
                            onClick={() => handleResolveAlert(alert.id)}
                        >
                            Resolve
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ActiveAlerts;
